/* eslint-disable no-unsafe-optional-chaining */
import {
  Card,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import React, { useState, useEffect } from "react";
import MDTypography from "components/MDTypography";
import moment from "moment";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import { calculateKpiPoints, formatTimeRemaining, numberFormatter } from "utils/HelperFunctions";
import KeyIcon from "@mui/icons-material/Key";
import MDBadgeDot from "components/MDBadgeDot";
import MDButton from "components/MDButton";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import CompetitionInfoPopover from "./CompetitionInfoPopover";
import DailyPointsInformationModal from "./DailyPointsInformationModal";

function UserCompetition({
  competition,
  kpiLocksDict,
  removeCompetition,
  handleActionEdit,
  setCompetitions,
  competitionIndex,
}) {
  const theme = useTheme();
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [months, setMonths] = useState(0);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [timeReachedToZero, setTimeReachedToZero] = useState(false);
  const [isCompetitionPointsInfoOpen, setIsCompetitionPointsInfoOpen] = useState(false);
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));

  useEffect(() => {
    const interval = setInterval(() => {
      const then = moment.tz(competition.endDatetime, loginUser.timezone);
      const now = moment.tz(undefined, loginUser.timezone);
      const countdown = moment.duration(then.diff(now));
      const monthsValue = countdown.months();
      const daysValue = countdown.days();
      const hoursValue = countdown.hours();
      const minutesValue = countdown.minutes();
      const secondsValue = countdown.seconds();

      setMonths(monthsValue);
      setDays(daysValue);
      setHours(hoursValue);
      setMinutes(minutesValue);
      setSeconds(secondsValue);
      if (daysValue <= 0 && hoursValue <= 0 && minutesValue <= 0 && secondsValue <= 0) {
        setTimeReachedToZero(true);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (timeReachedToZero) {
      removeCompetition(competition.id);
      enqueueSnackbar(`Competition: ${competition.name} has ended`, {
        variant: "info",
        autoHideDuration: 5000,
      });
    }
  }, [timeReachedToZero]);

  const handleCountChange = (row, count) => {
    setCompetitions((prevState) => {
      const prevCompetitions = [...prevState];
      const actionIndex = prevCompetitions[competitionIndex].actions.findIndex(
        (action) => action.kpiActionId === row.kpiActionId
      );
      const foundAction = prevCompetitions[competitionIndex].actions.find(
        (action) => action.kpiActionId === row.kpiActionId
      );

      if (actionIndex !== -1) {
        const actionData = {
          ...foundAction,
          CompetitionAction: {
            count,
            points: foundAction.CompetitionAction.points,
          },
        };

        if (typeof actionData.prevCount === "undefined") {
          actionData.prevCount = foundAction.CompetitionAction.count;
        }

        prevCompetitions[competitionIndex].actions[actionIndex] = actionData;
      }

      return prevCompetitions;
    });
  };

  const timeRemainingStr = formatTimeRemaining({
    days,
    hours,
    minutes,
    seconds,
    months,
  });

  const renderKpiInput = (row) => (
    <Stack direction="row" alignItems="center" spacing={0}>
      <MDButton
        disabled={row.disabled || kpiLocksDict[row.kpiActionId]}
        sx={{
          cursor: "pointer",
          minHeight: "unset",
          minWidth: "unset",
          width: "1rem",
          height: "100%",
          "&:hover": {
            background: `${theme.palette.trendRed.main}10`,
          },
          mr: "0.25rem",
        }}
        onClick={() => {
          if (row?.CompetitionAction?.count - 1 >= 0) {
            handleActionEdit({
              kpiActionId: row.kpiActionId,
              count: row?.CompetitionAction?.count - 1,
              points: calculateKpiPoints(row.count, row.points, row?.CompetitionAction.count - 1),
              disabled: true,
              call: true,
              action: "decrement",
              competitionId: competition.id,
            });
          }
        }}
      >
        <RemoveCircleOutline sx={{ color: theme.palette.trendRed.main }} />
      </MDButton>
      <TextField
        type="number"
        value={row?.CompetitionAction?.count?.toString()}
        inputProps={{ style: { textAlign: "center" } }}
        onMouseEnter={(e) =>
          e.target.addEventListener("wheel", (event) => {
            event.preventDefault();
          })
        }
        disabled={row.disabled || kpiLocksDict[row.kpiActionId]}
        sx={{
          width: "50%",
          maxWidth: "80px",
          backgroundColor: "#f3f3f3",
          borderRadius: "0.5rem",
          "& input[type=number]": {
            MozAppearance: "textfield",
            height: ".8em",
            fontSize: "1rem",
          },
          "& input[type=number]::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
            fontSize: "1rem",
          },
          "& input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
            fontSize: "1rem",
          },
          "& fieldset": { border: "none" },
          "& .MuiTextField-root": { ml: 0 },
        }}
        onChange={(event) => {
          if (event.target.value < 0) return;
          if (parseInt(event.target.value || 0, 10) > 999) {
            handleCountChange(row, 999);
          }
          if (parseInt(event.target.value || 0, 10) <= 999) {
            handleCountChange(row, parseInt(event.target.value || 0, 10));
          }
        }}
        onBlur={(event) => {
          if (
            parseInt(event.target.value || 0, 10) !==
            (typeof row?.prevCount !== "undefined" ? row?.prevCount : row?.CompetitionAction?.count)
          ) {
            handleActionEdit({
              kpiActionId: row.kpiActionId,
              count: parseInt(event.target.value || 0, 10),
              points: calculateKpiPoints(row.count, row.points, row?.CompetitionAction?.count),
              disabled: true,
              call: true,
              action: "change",
              oldCount:
                typeof row?.prevCount !== "undefined"
                  ? row?.prevCount
                  : row?.CompetitionAction?.count,
              competitionId: competition.id,
            });
          }
        }}
      />
      <MDButton
        disabled={row.disabled || kpiLocksDict[row.kpiActionId]}
        sx={{
          cursor: "pointer",
          minHeight: "unset",
          minWidth: "unset",
          width: "1rem",
          height: "100%",
          "&:hover": {
            background: `${theme.palette.trendGreen.main}10`,
          },
          ml: "0.25rem",
        }}
        onClick={() => {
          if (row?.CompetitionAction?.count + 1 > 999) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: "Maximum 3 Digits Number Are Allowed.",
              }),
              {
                variant: "custom",
              }
            );
          } else {
            handleActionEdit({
              kpiActionId: row.kpiActionId,
              count: row?.CompetitionAction?.count + 1,
              points: calculateKpiPoints(row.count, row.points, row?.CompetitionAction?.count + 1),
              disabled: true,
              call: true,
              action: "increment",
              competitionId: competition.id,
            });
          }
        }}
      >
        <AddCircleOutline sx={{ color: theme.palette.trendGreen.main }} />
      </MDButton>
    </Stack>
  );

  return (
    <Grid container spacing={3} mt={2} className="home-competition-grid">
      <Grid item xs={12}>
        <Card>
          <Grid container>
            <Grid item xs={2} sm={2} md={2} className="badge-grid">
              <MDBox className="image-wrapper">
                <img src={competition?.Badge?.badgeUrl} alt={competition?.Badge?.name} />
              </MDBox>
            </Grid>
            <Grid item xs={8} sm={8} md={8} mt={1}>
              <Stack alignItems="center">
                <MDTypography
                  sx={{
                    wordBreak: "break-word",
                    fontSize: "20px",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                >
                  {competition?.name}
                </MDTypography>
                <MDTypography sx={{ wordBreak: "break-word", fontSize: "16px" }}>
                  Competition
                </MDTypography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={2}
              sm={2}
              md={2}
              mt={1}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                "& .MuiSvgIcon-root": {
                  color: "#1368d9",
                },
              }}
            >
              <CompetitionInfoPopover competition={competition} />
            </Grid>
          </Grid>
          <Stack p={2} sx={{ overflowY: "auto" }} justifyContent="center">
            {competition?.endDatetime && (
              <Box display="inline-flex" alignItems="baseline">
                <MDTypography sx={{ fontSize: "16px", fontWeight: "600", pr: "1rem" }}>
                  Time Remaining:
                </MDTypography>
                <MDTypography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    textAlign: "center",
                    color: "#1368d9",
                  }}
                >
                  {timeRemainingStr}
                </MDTypography>
              </Box>
            )}
            <Box display="inline-flex" alignItems="baseline">
              <MDTypography sx={{ fontSize: "16px", fontWeight: "600", pr: "1rem" }}>
                Current Rank:
              </MDTypography>
              <MDTypography
                sx={{
                  fontSize: "32px",
                  fontWeight: "600",
                  textAlign: "center",
                  color: "#e91e63",
                  lineHeight: "2rem",
                }}
              >
                {competition?.rank}
                <MDBox component="span" sx={{ fontSize: "22px" }}>
                  /
                </MDBox>
                <MDBox component="span" sx={{ fontSize: "18px" }}>
                  {competition?.totalMembers}
                </MDBox>
              </MDTypography>
            </Box>
            {competition.winCondition === "Total Points" && (
              <Box display="flex" sx={{ pt: "0.2rem" }}>
                <MDTypography sx={{ fontSize: "0.9rem", fontWeight: "400", pr: "0.5rem" }}>
                  Competition Key:
                </MDTypography>
                <KeyIcon
                  onClick={() => setIsCompetitionPointsInfoOpen(true)}
                  sx={{
                    cursor: "pointer",
                    "&.MuiSvgIcon-root": {
                      fontSize: "28px !important",
                      color: `${theme.palette.iconBlue.main} !important`,
                    },
                    pb: "5px",
                  }}
                />
              </Box>
            )}
          </Stack>
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              sx={{ height: "100%", userSelect: "none", overflowY: "auto" }}
            >
              <Table aria-label="simple table">
                {!isMobileDisplay && (
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell>
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          KPI Name
                        </MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        <MDTypography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            color: "#888",
                          }}
                        >
                          Completed
                        </MDTypography>
                      </TableCell>
                      {competition.winCondition === "Total Points" && (
                        <TableCell align="center">
                          <MDTypography
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: "400",
                              color: "#888",
                            }}
                          >
                            Achieved Points
                          </MDTypography>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {competition.actions
                    ?.sort((a, b) => (a.KpiActivity?.name > b.KpiActivity?.name ? 1 : -1))
                    .map((row) => (
                      <TableRow key={`KpiActivity-${row.kpiActionId}`}>
                        {isMobileDisplay ? (
                          <>
                            <TableCell
                              sx={{
                                padding: "0.4rem 0.25rem 0.4rem 1rem",
                              }}
                            >
                              <Grid container direction="column">
                                <Grid item display="flex" justifyContent="space-between">
                                  <Box display="flex" alignItems="baseline">
                                    <MDBadgeDot
                                      color="warning"
                                      size="lg"
                                      sx={{
                                        p: 0.5,
                                        i: {
                                          background: generateColorFromString(
                                            row.KpiActivity?.name
                                          ),
                                        },
                                      }}
                                      badgeContent=""
                                    />
                                    <MDTypography
                                      title={row?.KpiActivity?.name}
                                      sx={{
                                        fontSize: "1rem",
                                        fontWeight: "300",
                                        lineHeight: "1.4rem",
                                      }}
                                    >
                                      {row?.KpiActivity?.name}
                                    </MDTypography>
                                  </Box>
                                </Grid>
                                <Grid item display="flex">
                                  {renderKpiInput(row)}
                                </Grid>
                              </Grid>
                            </TableCell>
                            {competition.winCondition === "Total Points" && (
                              <TableCell sx={{ padding: "0.4rem 1rem" }} align="center">
                                <Box display="inline-flex" alignItems="baseline" pr="1rem">
                                  <Typography variant="h3" sx={{ fontWeight: "400" }}>
                                    {numberFormatter(parseInt(row?.CompetitionAction?.points, 10))}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.7rem", pl: "0.25rem" }}>
                                    {row?.CompetitionAction?.points &&
                                    parseInt(row?.CompetitionAction?.points, 10) === 1
                                      ? "pt"
                                      : "pts"}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                          </>
                        ) : (
                          <>
                            <TableCell
                              sx={{
                                padding: "0.4rem 0.25rem 0.4rem 1rem",
                              }}
                            >
                              <Grid container>
                                <MDBadgeDot
                                  color="warning"
                                  size="lg"
                                  sx={{
                                    p: 0.5,
                                    i: {
                                      background: generateColorFromString(row.KpiActivity?.name),
                                    },
                                  }}
                                  badgeContent=""
                                />
                                <MDTypography sx={{ fontSize: "1rem", fontWeight: "300" }}>
                                  {row?.KpiActivity?.name}
                                </MDTypography>
                              </Grid>
                            </TableCell>
                            <TableCell sx={{ padding: "0.4rem 1rem" }} width="200px" align="center">
                              {renderKpiInput(row)}
                            </TableCell>
                            {competition.winCondition === "Total Points" && (
                              <TableCell sx={{ padding: "0.4rem 1rem" }} align="center">
                                {parseInt(row?.CompetitionAction?.points, 10)}
                              </TableCell>
                            )}
                          </>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Card>
      </Grid>
      <DailyPointsInformationModal
        handleClose={() => setIsCompetitionPointsInfoOpen((prev) => !prev)}
        open={isCompetitionPointsInfoOpen}
        actionInfo={competition.actions}
        competition
      />
    </Grid>
  );
}

export default UserCompetition;
